import React from "react";
import { Button, ButtonPulldown } from "../Button";
import { Divider } from "./Divider";
import FieldError from "../../FieldError";
import { validateWithZod } from "../../../utils/zod-utils";
import { CpapTreatmentSummaryUpsertBody } from "../../../lib/api/generated-zod/endpoints/cpap-prescription/cpap-prescription.zod";
import { useCpapTreatmentSummaryUpsert } from "../../../lib/api/generated/sASOnlineClinicBackendAPI";
import StatusScreen from "../../StatusScreen";
import { messages } from "../../../utils/i18n/messages";

export type TreatmentSummaryRegistrationProps = {
  patientId: string;
  clinicOrganizationId: string | null;
};

const MONTH_OPTIONS = [
  "1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12",
];

function toOptionalInt(value: string): number | undefined {
  const trimmed = value.trim();
  if (trimmed === "") return undefined;
  const parsed = Number(trimmed);
  return Number.isInteger(parsed) ? parsed : undefined;
}

const TREATMENT_SUMMARY_MESSAGES = messages.BIZSOL.PATIENT_DETAIL.TREATMENT_SUMMARY;
const VALIDATION_MESSAGES = TREATMENT_SUMMARY_MESSAGES.VALIDATION;

// zodのデフォルトメッセージ（英語）を、フィールドごとに定義した日本語メッセージへ置き換える。
// zod自体の判定結果（成功/失敗）は変更しない。
function toJapaneseFieldErrors(
  errors: Record<string, string>,
): Record<string, string> {
  const fieldMessageMap: Record<string, string> = {
    targetYear: VALIDATION_MESSAGES.TARGET_YEAR,
    targetMonth: VALIDATION_MESSAGES.TARGET_MONTH,
    avgAhi: VALIDATION_MESSAGES.AVG_AHI,
    usageRate: VALIDATION_MESSAGES.USAGE_RATE,
    avgUsageHours: VALIDATION_MESSAGES.AVG_USAGE_HOURS,
  };
  const translated: Record<string, string> = {};
  for (const field of Object.keys(errors)) {
    translated[field] =
      fieldMessageMap[field] ?? TREATMENT_SUMMARY_MESSAGES.UPSERT.VALIDATION_FAILED;
  }
  return translated;
}

function toOptionalFloat(value: string): number | undefined {
  const trimmed = value.trim();
  if (trimmed === "") return undefined;
  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed)) return undefined;
  return Math.round(parsed * 100) / 100;
}

// 治療状況を登録（CPAP治療実績: 平均AHI・装着率・平均使用時間の月次手動登録）
export function TreatmentSummaryRegistration({
  patientId,
  clinicOrganizationId,
}: TreatmentSummaryRegistrationProps) {
  const [targetYear, setTargetYear] = React.useState<string>("");
  const [targetMonth, setTargetMonth] = React.useState<string>("");
  const [avgAhi, setAvgAhi] = React.useState<string>("");
  const [usageRate, setUsageRate] = React.useState<string>("");
  const [usageHour, setUsageHour] = React.useState<string>("");
  const [usageMinute, setUsageMinute] = React.useState<string>("");
  const [usageSecond, setUsageSecond] = React.useState<string>("");
  const [errors, setErrors] = React.useState<Record<string, string>>({});
  const [submitMessage, setSubmitMessage] = React.useState<string>("");
  const [bannerMessage, setBannerMessage] = React.useState<string>("");
  const mutation = useCpapTreatmentSummaryUpsert();

  const buildBody = () => {
    const hour = toOptionalInt(usageHour) ?? 0;
    const minute = toOptionalInt(usageMinute) ?? 0;
    const second = toOptionalInt(usageSecond) ?? 0;
    const avgUsageHours = Math.round((hour + minute / 60 + second / 3600) * 100) / 100;

    return {
      patientId,
      targetYear: toOptionalInt(targetYear),
      targetMonth: toOptionalInt(targetMonth),
      avgAhi: toOptionalFloat(avgAhi),
      usageRate: toOptionalFloat(usageRate),
      avgUsageHours,
    };
  };

  const handleSubmit = () => {
    const body = buildBody();
    const res = validateWithZod(CpapTreatmentSummaryUpsertBody, body);
    if (!res.success) {
      setErrors(toJapaneseFieldErrors(res.errors));
      return;
    }
    setErrors({});
    setSubmitMessage("");
    setBannerMessage("");
    mutation.mutate(
      { data: res.data },
      {
        onSuccess: () => {
          setSubmitMessage("登録しました。");
        },
        onError: (err: any) => {
          const status = Number(err?.status);
          const upsertMessages = TREATMENT_SUMMARY_MESSAGES.UPSERT;
          if (status === 403) {
            setBannerMessage(upsertMessages.FORBIDDEN);
            return;
          }
          if (status === 404) {
            setBannerMessage(upsertMessages.NOT_FOUND);
            return;
          }
          if (status === 422) {
            // サーバーが返す detail（生の英語/日本語混在テキスト）は表示せず、
            // messages に定義した日本語の文言を使う。
            setBannerMessage(upsertMessages.VALIDATION_FAILED);
            return;
          }
          setBannerMessage(upsertMessages.FAILED);
        },
      },
    );
  };

  if (!clinicOrganizationId) {
    return (
      <p className="text-textDefault text-[14px]">
        クリニックが特定できないため登録できません。
      </p>
    );
  }

  return (
    <>
      <div className="w-full flex flex-col gap-s-100">
        <p className="text-textDefault text-[14px] font-semibold">
          CPAP治療状況（平均AHI・装着率・平均使用時間）を登録します。
        </p>
        <p className="text-textDefault text-[12px]">
          対象年月を選択し、必要な数値を入力して「登録」をクリックしてください。同じ対象年月に既存のデータがある場合は上書きされます。
        </p>
      </div>

      <div className="w-full flex flex-col gap-s-300">
        <div className="w-full flex items-center gap-[10px]">
          <div className="w-[110px] h-[36px] flex items-center">
            <p className="text-textDefault text-[14px]">・対象年</p>
          </div>
          <input
            aria-label="対象年"
            type="text"
            inputMode="numeric"
            maxLength={4}
            value={targetYear}
            onChange={(e) => setTargetYear(e.target.value)}
            className="w-[120px] h-[36px] border border-black rounded px-2"
            placeholder="2026"
          />
          <FieldError
            id="targetYear-error"
            isVisible={Boolean(errors.targetYear)}
            message={errors.targetYear ?? ""}
            className="text-red-600 text-xs mt-1"
          />
        </div>

        <div className="w-full flex items-center gap-[10px]">
          <div className="w-[110px] h-[36px] flex items-center">
            <p className="text-textDefault text-[14px]">・対象月</p>
          </div>
          <ButtonPulldown
            label="選択してください"
            options={MONTH_OPTIONS}
            value={targetMonth}
            onChange={setTargetMonth}
            containerClassName="w-[160px] h-[36px] border-black"
          />
          <FieldError
            id="targetMonth-error"
            isVisible={Boolean(errors.targetMonth)}
            message={errors.targetMonth ?? ""}
            className="text-red-600 text-xs mt-1"
          />
        </div>

        <div className="w-full flex items-center gap-[10px]">
          <div className="w-[110px] h-[36px] flex items-center">
            <p className="text-textDefault text-[14px]">・平均AHI</p>
          </div>
          <input
            aria-label="平均AHI"
            type="text"
            inputMode="decimal"
            value={avgAhi}
            onChange={(e) => setAvgAhi(e.target.value)}
            className="w-[120px] h-[36px] border border-black rounded px-2"
            placeholder="5.0"
          />
          <FieldError
            id="avgAhi-error"
            isVisible={Boolean(errors.avgAhi)}
            message={errors.avgAhi ?? ""}
            className="text-red-600 text-xs mt-1"
          />
        </div>

        <div className="w-full flex items-center gap-[10px]">
          <div className="w-[110px] h-[36px] flex items-center">
            <p className="text-textDefault text-[14px]">・装着率(%)</p>
          </div>
          <input
            aria-label="装着率"
            type="text"
            inputMode="decimal"
            value={usageRate}
            onChange={(e) => setUsageRate(e.target.value)}
            className="w-[120px] h-[36px] border border-black rounded px-2"
            placeholder="90"
          />
          <FieldError
            id="usageRate-error"
            isVisible={Boolean(errors.usageRate)}
            message={errors.usageRate ?? ""}
            className="text-red-600 text-xs mt-1"
          />
        </div>

        <div className="w-full flex items-center gap-[10px]">
          <div className="w-[110px] h-[36px] flex items-center">
            <p className="text-textDefault text-[14px]">・平均使用時間</p>
          </div>
          <input
            aria-label="平均使用時間（時）"
            type="text"
            inputMode="numeric"
            value={usageHour}
            onChange={(e) => setUsageHour(e.target.value)}
            className="w-[70px] h-[36px] border border-black rounded px-2"
            placeholder="6"
          />
          <span className="text-textDefault text-[14px]">時間</span>
          <input
            aria-label="平均使用時間（分）"
            type="text"
            inputMode="numeric"
            value={usageMinute}
            onChange={(e) => setUsageMinute(e.target.value)}
            className="w-[70px] h-[36px] border border-black rounded px-2"
            placeholder="30"
          />
          <span className="text-textDefault text-[14px]">分</span>
          <input
            aria-label="平均使用時間（秒）"
            type="text"
            inputMode="numeric"
            value={usageSecond}
            onChange={(e) => setUsageSecond(e.target.value)}
            className="w-[70px] h-[36px] border border-black rounded px-2"
            placeholder="0"
          />
          <span className="text-textDefault text-[14px]">秒</span>
          <FieldError
            id="avgUsageHours-error"
            isVisible={Boolean(errors.avgUsageHours)}
            message={errors.avgUsageHours ?? ""}
            className="text-red-600 text-xs mt-1"
          />
        </div>
      </div>

      <Divider />

      <div className="w-full flex items-center justify-end gap-[16px]">
        <Button
          label="登録"
          variant="solid"
          className="gap-0"
          onClick={handleSubmit}
          disabled={mutation.isPending}
        />
      </div>
      <div className="w-full text-right">
        {bannerMessage && (
          <StatusScreen type="error" message={bannerMessage} fullScreen={false} />
        )}
        {submitMessage && (
          <StatusScreen type="success" message={submitMessage} fullScreen={false} />
        )}
      </div>
    </>
  );
}

export default TreatmentSummaryRegistration;
